<template>
  <span
    v-if="tone"
    class="inline-flex items-center gap-1 leading-none"
    :class="TONE_CLASS[tone]"
    :title="vsDate ? `vs ${vsDate}` : undefined"
    :aria-label="ariaLabelText"
  >
    <svg v-if="tone === 'flat'" :class="arrowSizeClass" viewBox="0 0 14 6" fill="currentColor" aria-hidden="true">
      <rect x="0" y="1" width="14" height="4" rx="2" />
    </svg>
    <!-- Down = the up arrow flipped, so both share one shape. -->
    <svg
      v-else
      :class="[arrowSizeClass, tone === 'down' && 'rotate-180']"
      viewBox="0 0 10 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M5 0 L10 6 L0 6 Z" />
      <rect x="3" y="6" width="4" height="10" rx="1" />
    </svg>
    <span v-if="!arrowOnly" :class="textSizeClass">{{ formatOtsDelta(delta as number) }}</span>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { formatOtsDelta, otsDeltaTone } from '@/utils/otsKpiPresentation'

const props = withDefaults(
  defineProps<{
    /** Day-over-day OTS % delta (points); null/undefined renders nothing. */
    delta: number | null | undefined
    /** Previous snapshot date the delta compares against, for the tooltip/aria-label. */
    vsDate?: string | null
    /** lg = paired with the 3xl KPI percentage, sm = compact inline marker. */
    size?: 'sm' | 'lg'
    /** Arrow only, no delta text (tight spaces such as the sidebar summary). */
    arrowOnly?: boolean
  }>(),
  { vsDate: null, size: 'sm', arrowOnly: false }
)

const TONE_CLASS = {
  up: 'text-green-600 dark:text-green-400',
  down: 'text-red-600 dark:text-red-400',
  flat: 'text-text-muted'
} as const

const tone = computed(() => otsDeltaTone(props.delta))

const arrowSizeClass = computed(() => (props.size === 'lg' ? 'w-[14px] h-[14px]' : 'w-[9px] h-[9px] relative -top-px'))

const textSizeClass = computed(() => (props.size === 'lg' ? 'text-sm font-semibold' : 'text-[10px] font-semibold'))

/** e.g. "up 2.10 points vs 2026-09-20" */
const ariaLabelText = computed(() => {
  const vs = props.vsDate ? ` vs ${props.vsDate}` : ''
  return `${tone.value} ${Math.abs(Math.round((props.delta as number) * 100) / 100).toFixed(2)} points${vs}`
})
</script>
